import React, { useEffect, useRef, useState } from 'react';
import { cmToInchParts, inchPartsToCmRoundUp, InchParts, Quarter } from '../helper';

type Props = {
  valueCm?: number;
  onChangeCm?: (cm: number) => void;
  onChangeParts?: (parts: InchParts) => void;
  disabled?: boolean;
  className?: string;
  rounding?: 'nearest' | 'floor' | 'ceil';
};

export const QuarterInchInput: React.FC<Props> = ({
  valueCm,
  onChangeCm,
  onChangeParts,
  disabled = false,
  className,
  rounding = 'nearest',
}) => {
  const [wholeStr, setWholeStr] = useState('');
  const [quarter, setQuarter] = useState<Quarter>(0);
  const [fractionFocused, setFractionFocused] = useState(false);
  const [wholeFocused, setWholeFocused] = useState(false);

  const isFocusedRef = useRef(false);
  const wholeInputRef = useRef<HTMLInputElement | null>(null);
  const fractionInputRef = useRef<HTMLInputElement | null>(null);

  const focusEnd = (el: HTMLInputElement | null) => {
    if (!el) return;
    const len = el.value.length;
    requestAnimationFrame(() => {
      el.setSelectionRange(len, len);
    });
  };

  const emit = (wholeText: string, q: Quarter) => {
    const wholeNum = wholeText ? Number(wholeText) : 0;

    const parts: InchParts = {
      whole: wholeNum,
      quarter: q,
    };

    const cm = inchPartsToCmRoundUp(parts);
    onChangeParts?.(parts);
    onChangeCm?.(cm);
  };

  useEffect(() => {
    if (isFocusedRef.current) return;

    if (valueCm == null || !Number.isFinite(valueCm)) {
      setWholeStr('');
      setQuarter(0);
      return;
    }

    const parts = cmToInchParts(valueCm, rounding);

    setWholeStr(parts.whole > 0 ? String(parts.whole) : '');
    setQuarter(parts.quarter);
  }, [valueCm, rounding]);

  useEffect(() => {
    if (!wholeInputRef.current) return;
    const el = wholeInputRef.current;

    requestAnimationFrame(() => {
      el.scrollLeft = el.scrollWidth;
    });
  }, [wholeStr]);

  const stepQuarter = (dir: 1 | -1) => {
    const currentWholeNum = wholeStr ? Number(wholeStr) : 0;

    let totalQuarters = currentWholeNum * 4 + quarter + dir;

    if (totalQuarters < 0) {
      totalQuarters = 0;
    }

    const newWholeNum = Math.floor(totalQuarters / 4);
    const newQuarter = (totalQuarters % 4) as Quarter;

    const newWholeStr = newWholeNum > 0 ? String(newWholeNum) : '';

    setWholeStr(newWholeStr);
    setQuarter(newQuarter);
    emit(newWholeStr, newQuarter);
  };

  const handleWholeChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '');

    if (!digitsOnly) {
      setWholeStr('');
      emit('', quarter);
      return;
    }

    const normalized = digitsOnly.replace(/^0+(?=\d)/, '');
    setWholeStr(normalized);
    emit(normalized, quarter);
  };

  const numerator = quarter;
  const denominator = 4;

  return (
    <div className={`quarter-inch-input ${className ?? ''} ${disabled ? 'is-disabled' : ''}`}>
      <div className="quarter-inch-input__inner">
        <input
          ref={wholeInputRef}
          className={`quarter-inch-input__whole ${wholeFocused ? 'quarter-inch-input__whole--active' : ''}`}
          disabled={disabled}
          type="text"
          inputMode="numeric"
          value={wholeStr}
          onFocus={(e) => {
            isFocusedRef.current = true;
            setWholeFocused(true);
            focusEnd(e.currentTarget);
          }}
          onBlur={() => {
            isFocusedRef.current = false;
            setWholeFocused(false);
          }}
          onChange={(e) => {
            handleWholeChange(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              return;
            }
          }}
        />

        <div className="quarter-inch-input__fraction">
          <span className="quarter-inch-input__pipe" />

          <div className={`quarter-inch-input__fraction-box ${fractionFocused ? 'is-active' : ''}`}>
            <input
              ref={fractionInputRef}
              className="quarter-inch-input__fraction-input"
              disabled={disabled}
              readOnly
              onFocus={(e) => {
                isFocusedRef.current = true;
                setFractionFocused(true);
                focusEnd(e.currentTarget);
              }}
              onBlur={() => {
                isFocusedRef.current = false;
                setFractionFocused(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  stepQuarter(1);
                  return;
                }

                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  stepQuarter(-1);
                  return;
                }

                if (e.key === 'Tab') {
                  return;
                }

                e.preventDefault();
              }}
            />

            <div className="quarter-inch-input__fraction-display">
              <span className="quarter-inch-input__fraction-num">{numerator}</span>
              <span className="quarter-inch-input__fraction-slash">/</span>
              <span className="quarter-inch-input__fraction-den">{denominator}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
